import { Input, MessageIcon, MMButton } from '@components'
import cn from '@integrations/classnames'
import { useRef, useState } from '@integrations/react'
import { uiSize } from '@utils'
import { useCreateTicketWithMessagesMutation, useGetTicketCategoriesQuery } from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import {
  MAX_ATTACHMENT_SIZE_BYTES,
  MAX_ATTACHMENT_SIZE_MB,
  MAX_INITIAL_MESSAGES,
  MAX_MESSAGE_LENGTH,
  MAX_TOTAL_MESSAGE_LENGTH,
  validateFiles
} from '../../utils'
import ComposeArea, { type ComposeAreaHandle, ComposeCharCount } from '../ComposeArea/ComposeArea'
import { buildTicketWithMessagesPayload, countMessageSlots, type MessageDraft } from './buildTicketWithMessagesPayload'
import styles from './CreateTicketModal.module.scss'

interface Props {
  onSuccess: (ticketId: number) => void
  onClose: () => void
  onCloseAfterSuccess?: (ticketId: number) => void
}

function CreateTicketModal({ onSuccess, onClose, onCloseAfterSuccess }: Props) {
  const [submitted, setSubmitted] = useState(false)
  const [submittedId, setSubmittedId] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [categoryId, setCategoryId] = useState<number | ''>('')
  const [sentMessages, setSentMessages] = useState<MessageDraft[]>([])
  const [composeText, setComposeText] = useState('')
  const [composeFiles, setComposeFiles] = useState<File[]>([])
  const [fileErrors, setFileErrors] = useState<string[]>([])
  const [dragActive, setDragActive] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const composeRef = useRef<ComposeAreaHandle>(null)

  const [createTicketWithMessages, { isLoading: createFetching }] = useCreateTicketWithMessagesMutation()
  const { data: categories } = useGetTicketCategoriesQuery()
  const categoryOptions =
    categories?.map((category) => ({ label: category.description, value: String(category.id) })) ?? []

  const committedMessageSlots = countMessageSlots(sentMessages)
  const totalMessageCharacters = sentMessages.reduce((sum, msg) => sum + msg.text.length, 0) + composeText.length

  const hasUnsavedContent =
    title.trim() || categoryId !== '' || sentMessages.length > 0 || composeText.trim() || composeFiles.length > 0

  const canSubmit =
    title.trim() !== '' &&
    categoryId !== '' &&
    sentMessages.length > 0 &&
    composeText.trim() === '' &&
    composeFiles.length === 0
  const canAddMessage = composeText.trim() !== '' || composeFiles.length > 0

  function handleClose() {
    if (hasUnsavedContent) {
      if (
        !window.confirm(
          tr(
            'FRONTEND.SUPPORT.CREATE.DISCARD_CONFIRM',
            'Are you sure you want to discard your request? Your changes will be lost.'
          )
        )
      ) {
        return
      }
    }
    onClose()
  }

  function handleComposeKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && e.shiftKey) return
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddMessage()
    }
  }

  function handleAddMessage() {
    if (!canAddMessage) return
    if (composeText.length > MAX_MESSAGE_LENGTH) return
    const draftSlots = composeFiles.length > 0 ? composeFiles.length : 1
    if (committedMessageSlots + draftSlots > MAX_INITIAL_MESSAGES) return
    setSentMessages((prev) => [...prev, { text: composeText, files: composeFiles }])
    setComposeText('')
    setComposeFiles([])
    setFileErrors([])
  }

  function addFiles(files: File[]) {
    const { valid, errors } = validateFiles(files)
    const allErrors = [...errors]
    const currentTotal = composeFiles.reduce((sum, f) => sum + f.size, 0)
    const newTotal = valid.reduce((sum, f) => sum + f.size, 0)
    if (currentTotal + newTotal > MAX_ATTACHMENT_SIZE_BYTES) {
      allErrors.push(
        tr('FRONTEND.SUPPORT.ERROR.TOTAL_SIZE', `Total attachments exceed the ${MAX_ATTACHMENT_SIZE_MB}MB limit`)
      )
      setFileErrors(allErrors)
      return
    }
    const draftSlots = composeFiles.length + valid.length
    if (committedMessageSlots + draftSlots > MAX_INITIAL_MESSAGES) {
      allErrors.push(
        tr(
          'FRONTEND.SUPPORT.ERROR.TOO_MANY_MESSAGES',
          `Adding these files would exceed the ${MAX_INITIAL_MESSAGES}-message limit`
        )
      )
      setFileErrors(allErrors)
      return
    }
    setFileErrors(allErrors)
    if (valid.length > 0) setComposeFiles((prev) => [...prev, ...valid])
  }

  function removeComposeFile(index: number) {
    setComposeFiles((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit() {
    if (!canSubmit) return
    setSubmitError(null)
    try {
      // canSubmit already requires an empty compose box, so handleSubmit only ever runs with
      // committed drafts -- no in-progress draft can be included here.
      const { messages, fileMessageMapping, files } = buildTicketWithMessagesPayload(sentMessages)

      const ticket = await createTicketWithMessages({
        ticket: {
          title: title.trim(),
          categoryId: Number(categoryId),
          messages,
          fileMessageMapping
        },
        files
      }).unwrap()

      setSubmittedId(ticket.id as number)
      setSubmitted(true)
    } catch (err: unknown) {
      const e = err as {
        error?: string
        statusCode?: number
        status?: number
        cause?: { field?: string; cause?: string }[]
      }
      const httpStatus = e?.statusCode ?? e?.status
      if (httpStatus === 429) {
        setSubmitError(
          tr(
            'FRONTEND.SUPPORT.ERROR.RATE_LIMIT_CREATE',
            "You've reached the daily limit of 10 support requests. Please try again tomorrow."
          )
        )
      } else {
        // `||`, not `??`, is intentional: an empty-string error from BE should also fall through
        // to the fallback text, not render a blank ErrorDisplay. (mmfintech-backend-api's error
        // shape never includes a `data` property -- that branch was dead code, removed.)
        const message =
          e?.error || tr('FRONTEND.SUPPORT.ERROR.CREATE_FAILED', 'Something went wrong while submitting your request.')
        // Submitting N messages that all fail the same validation rule (e.g. disallowed
        // characters) returns N near-identical `cause` entries, one per message index --
        // dedupe by the cause text itself, and drop the field/JSON-path prefix (e.g.
        // "messages[0].message"), which is an internal detail and not useful to the user.
        const causeDetail = Array.from(
          new Set((Array.isArray(e?.cause) ? e.cause : []).map((c) => c.cause).filter((c): c is string => Boolean(c)))
        ).join('; ')
        setSubmitError(causeDetail ? `${message} (${causeDetail})` : message)
      }
    }
  }

  function resetForm() {
    setSubmitted(false)
    setSubmittedId(null)
    setTitle('')
    setCategoryId('')
    setSentMessages([])
    setComposeText('')
    setComposeFiles([])
    setFileErrors([])
    setDragActive(false)
    setSubmitError(null)
  }

  if (submitted && submittedId) {
    return (
      <div className={cn(styles['ctm-overlay'], 'ctm-overlay')}>
        <div className={cn(styles['ctm-modal'], 'ctm-modal', styles['ctm-success'], 'ctm-success')}>
          <div className={cn(styles['ctm-success-icon'], 'ctm-success-icon')}>✓</div>
          <h2 className={cn(styles['ctm-success-title'], 'ctm-success-title')}>
            {tr('FRONTEND.SUPPORT.CREATE.SUCCESS_TITLE', 'Request Submitted!')}
          </h2>
          <div className={cn(styles['ctm-success-actions'], 'ctm-success-actions')}>
            <MMButton
              type='button'
              variant='secondary'
              caption={tr('FRONTEND.SUPPORT.CREATE.SUBMIT_ANOTHER', 'Submit another ticket')}
              onClick={resetForm}
            />
            <MMButton
              type='button'
              variant='secondary'
              caption={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
              onClick={() => (onCloseAfterSuccess ? onCloseAfterSuccess(submittedId) : onClose())}
            />
            <MMButton
              type='button'
              variant='primary'
              caption={tr('FRONTEND.SUPPORT.CREATE.VIEW_TICKET', 'Go to your ticket')}
              onClick={() => onSuccess(submittedId)}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(styles['ctm-overlay'], 'ctm-overlay')}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <div className={cn(styles['ctm-modal'], 'ctm-modal')}>
        <div className={cn(styles['ctm-header'], 'ctm-header')}>
          <span className={cn(styles['ctm-title'], 'ctm-title')}>
            {tr('FRONTEND.SUPPORT.CREATE.TITLE', 'Create new ticket')}
          </span>
          <MMButton
            type='button'
            variant='secondary'
            collapsed
            collapsedIcon={<span className={cn(styles['ctm-close'], 'ctm-close')}>×</span>}
            title={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
            onClick={handleClose}
          />
        </div>

        <div className={cn(styles['ctm-body'], 'ctm-body')}>
          <div className={cn(styles['ctm-left'], 'ctm-left')}>
            <Input
              type='text'
              name='title'
              label={tr('FRONTEND.SUPPORT.CREATE.TITLE_LABEL', 'Title')}
              required
              maxLength={25}
              value={title}
              onChange={(_name: string, value: string) => setTitle(value)}
              placeholder={tr('FRONTEND.SUPPORT.CREATE.TITLE_PLACEHOLDER', 'Brief description of your issue')}
              hideErrorLine
            />

            <Input
              type='select'
              name='category'
              label={tr('FRONTEND.SUPPORT.CREATE.CATEGORY_LABEL', 'Category')}
              required
              value={categoryId === '' ? '' : String(categoryId)}
              options={categoryOptions}
              onChange={(_name: string, value: string) => setCategoryId(value ? Number(value) : '')}
              placeholder={tr('FRONTEND.SUPPORT.CREATE.CATEGORY_PLACEHOLDER', 'Please, select')}
              isSearchable={false}
              dropdownPortal
              hideErrorLine
            />

            <p className={cn(styles['ctm-hint'], 'ctm-hint')}>
              {tr(
                'FRONTEND.SUPPORT.CREATE.HINT',
                'Fill in the title and category, then compose your messages on the right before submitting.'
              )}
            </p>

            <ErrorDisplay error={submitError} />
          </div>

          <div
            className={cn(styles['ctm-right'], 'ctm-right', {
              [styles['drag-active']]: dragActive,
              'drag-active': dragActive
            })}
            onDragOver={(e) => {
              e.preventDefault()
              setDragActive(true)
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragActive(false)
              addFiles(Array.from(e.dataTransfer.files))
            }}
          >
            <div className={cn(styles['ctm-messages-header'], 'ctm-messages-header')}>
              <span className={cn(styles['ctm-messages-row-label'], 'ctm-messages-row-label')}>
                {tr('FRONTEND.SUPPORT.CREATE.MESSAGES_LABEL', 'Initial Messages')}
              </span>
              <span className={cn(styles['ctm-messages-count'], 'ctm-messages-count')}>
                ({committedMessageSlots}/{MAX_INITIAL_MESSAGES})
              </span>
              <span className={cn(styles['ctm-messages-hint'], 'ctm-messages-hint')}>
                {tr('FRONTEND.SUPPORT.CREATE.SHIFT_ENTER', 'Use Shift+Enter for new lines · Max 10 messages')}
              </span>
            </div>
            <div
              className={cn(
                styles['ctm-messages-header'],
                'ctm-messages-header',
                styles['ctm-messages-total-chars-row'],
                'ctm-messages-total-chars-row'
              )}
            >
              <span className={cn(styles['ctm-messages-row-label'], 'ctm-messages-row-label')}>
                {tr('FRONTEND.SUPPORT.CREATE.TOTAL_CHARS_LABEL', 'Total Characters')}
              </span>
              <span className={cn(styles['ctm-messages-count'], 'ctm-messages-count')}>
                ({totalMessageCharacters}/{MAX_TOTAL_MESSAGE_LENGTH})
              </span>
            </div>

            <div className={cn(styles['ctm-messages-area'], 'ctm-messages-area')}>
              {sentMessages.length === 0 ? (
                <div className={cn(styles['ctm-empty-messages'], 'ctm-empty-messages')}>
                  <MessageIcon size={32} strokeWidth='1.5' />
                  <span>{tr('FRONTEND.SUPPORT.CREATE.EMPTY_MESSAGES', 'Type messages below or drop files here')}</span>
                </div>
              ) : (
                sentMessages.map((msg, i) => (
                  <div key={`${msg.text}-${i}`} className={cn(styles['ctm-sent-message'], 'ctm-sent-message')}>
                    {msg.text && <div>{msg.text}</div>}
                    {msg.files.map((f) => (
                      <span key={f.name} className={cn(styles['ctm-sent-file'], 'ctm-sent-file')}>
                        📎 {f.name}
                      </span>
                    ))}
                  </div>
                ))
              )}
            </div>

            <div className={cn(styles['ctm-compose'], 'ctm-compose')}>
              <ComposeArea
                ref={composeRef}
                text={composeText}
                onTextChange={setComposeText}
                onKeyDown={handleComposeKeyDown}
                files={composeFiles}
                fileErrors={fileErrors}
                onDismissFileErrors={() => setFileErrors([])}
                onRemoveFile={removeComposeFile}
                onAddFiles={addFiles}
                placeholder={tr('FRONTEND.SUPPORT.CREATE.MESSAGE_PLACEHOLDER', 'Type a message...')}
                maxLength={MAX_MESSAGE_LENGTH}
                disabled={committedMessageSlots >= MAX_INITIAL_MESSAGES}
                inlineButtons={
                  <>
                    <MMButton
                      type='button'
                      variant='secondary'
                      size='small'
                      collapsed
                      collapsedIcon={
                        <svg
                          width={uiSize(16)}
                          height={uiSize(16)}
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                        >
                          <path d='M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48' />
                        </svg>
                      }
                      title={tr('FRONTEND.SUPPORT.CREATE.ATTACH_FILE', 'Attach file')}
                      data-test='attach-file-button'
                      onClick={() => composeRef.current?.openFilePicker()}
                      disabled={committedMessageSlots >= MAX_INITIAL_MESSAGES}
                    />
                    <MMButton
                      type='button'
                      variant='primary'
                      size='small'
                      collapsed
                      collapsedIcon={
                        <svg
                          width={uiSize(16)}
                          height={uiSize(16)}
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                        >
                          <line x1='22' y1='2' x2='11' y2='13' />
                          <polygon points='22 2 15 22 11 13 2 9 22 2' />
                        </svg>
                      }
                      title={tr('FRONTEND.SUPPORT.CREATE.ADD_MESSAGE', 'Add message')}
                      data-test='add-message-button'
                      onClick={handleAddMessage}
                      disabled={!canAddMessage || committedMessageSlots >= MAX_INITIAL_MESSAGES}
                    />
                  </>
                }
              />
              <ComposeCharCount length={composeText.length} max={MAX_MESSAGE_LENGTH} />
            </div>
          </div>
        </div>

        <div className={cn(styles['ctm-footer'], 'ctm-footer')}>
          <MMButton
            type='button'
            variant='secondary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={handleClose}
          />
          <MMButton
            type='button'
            variant='primary'
            caption={tr('FRONTEND.SUPPORT.CREATE.SUBMIT', 'Create Ticket')}
            loading={createFetching}
            data-test='create-ticket-button'
            onClick={handleSubmit}
            disabled={!canSubmit || createFetching}
          />
        </div>
      </div>
    </div>
  )
}

export default CreateTicketModal
